import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOTPVerification } from '../../hooks/useOTPVerification';

export default function VerifyOTPPage() {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const {
    email,
    otpCode,
    isVerifying,
    attemptsRemaining,
    canResend,
    resendCooldown,
    isResending,
    deviceInfo,
    handleOtpChange,
    verifyOTP,
    resendOTP,
  } = useOTPVerification();

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otpCode.length === 6 && !isVerifying) {
      verifyOTP();
    }
  }, [otpCode]);

  const handleInputChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = otpCode.split('');
    newCode[index] = digit;
    handleOtpChange(newCode.join(''));

    // Advance to next box on input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otpCode[index]) {
        // Clear current box
        const newCode = otpCode.split('');
        newCode[index] = '';
        handleOtpChange(newCode.join(''));
      } else if (index > 0) {
        // Move to previous box
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    handleOtpChange(paste);
    const focusIndex = Math.min(paste.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(b.length) + c)
    : '';

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center">
              <span className="text-xl sm:text-2xl font-bold">
                <span className="text-yellow-400">Nutri</span>
                <span className="text-white">Bot</span>
              </span>
            </Link>
            <Link
              to="/login"
              className="flex items-center gap-2 text-xs sm:text-sm text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>{t('auth.login.title')}</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="min-h-[calc(100vh-56px)] sm:min-h-[calc(100vh-64px)] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-md w-full">

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
              <i className="ri-mail-lock-line text-yellow-400 text-3xl"></i>
            </div>
          </div>

          {/* Headline */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
              {t('auth.otp.title')}
            </h1>
            <p className="text-gray-300 text-sm sm:text-base">
              {t('auth.otp.subtitle')}
            </p>
            <p className="text-yellow-400 font-semibold mt-1 text-sm sm:text-base">
              {maskedEmail}
            </p>
          </div>

          <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 border border-gray-800">

            {/* Device Info */}
            {deviceInfo && (
              <div className="flex items-center gap-3 bg-gray-800 rounded-lg px-4 py-3 mb-6">
                <i className={`${
                  deviceInfo.type === 'mobile' ? 'ri-smartphone-line' :
                  deviceInfo.type === 'tablet' ? 'ri-tablet-line' :
                  'ri-computer-line'
                } text-yellow-400 text-lg`}></i>
                <div>
                  <p className="text-xs text-gray-400">{t('auth.otp.verifyingDevice')}</p>
                  <p className="text-sm text-white font-medium">{deviceInfo.name}</p>
                </div>
              </div>
            )}

            {/* Attempts Warning */}
            {attemptsRemaining !== null && attemptsRemaining <= 2 && (
              <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-3 mb-6">
                <i className="ri-alert-line text-orange-400 flex-shrink-0"></i>
                <p className="text-orange-400 text-sm">
                  {attemptsRemaining === 1
                    ? t('auth.otp.oneAttemptLeft')
                    : t('auth.otp.attemptsLeft', { count: attemptsRemaining })}
                </p>
              </div>
            )}

            {/* OTP Input Boxes */}
            <div className="flex gap-2 sm:gap-3 justify-center mb-6">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otpCode[index] || ''}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isVerifying}
                  aria-label={`Digit ${index + 1}`}
                  className={`w-11 h-12 sm:w-12 sm:h-14 bg-gray-800 text-white text-xl sm:text-2xl font-bold text-center rounded-lg border-2 transition focus:outline-none disabled:opacity-50 ${
                    otpCode[index]
                      ? 'border-yellow-400 bg-gray-700'
                      : 'border-gray-700 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30'
                  }`}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={verifyOTP}
              disabled={otpCode.length < 6 || isVerifying}
              className="w-full py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-ball duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                  {t('auth.otp.verifying')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-shield-check-line text-lg"></i>
                  {t('auth.otp.verifyButton')}
                </span>
              )}
            </button>

            {/* Resend Section */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400 mb-2">{t('auth.otp.didntReceive')}</p>
              {canResend ? (
                <button
                  onClick={resendOTP}
                  disabled={isResending}
                  className="text-sm text-yellow-400 hover:text-yellow-300 font-semibold transition-colors cursor-pointer disabled:opacity-50"
                >
                  {isResending ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin"></i>
                      {t('auth.otp.sending')}
                    </span>
                  ) : (
                    t('auth.otp.resendButton')
                  )}
                </button>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('auth.otp.resendIn', { seconds: resendCooldown })}
                </p>
              )}
            </div>

            {/* Help Text */}
            <div className="mt-6 pt-6 border-t border-gray-800 space-y-1.5 text-center">
              <p className="text-xs text-gray-500">
                <i className="ri-spam-2-line mr-1.5"></i>
                {t('auth.otp.checkSpam')}
              </p>
              <p className="text-xs text-gray-500">
                <i className="ri-time-line mr-1.5"></i>
                {t('auth.otp.expiresIn')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}